import { useLayoutEffect, useMemo, useRef, useState, type KeyboardEvent } from 'react'
import { AnimatePresence, motion } from 'framer-motion'
import { createPortal } from 'react-dom'
import { useDashboardViewControlsSlot } from '../../context/dashboardViewControlsSlotCore'
import { CanvasOverlay } from '../Canvas/CanvasOverlay'
import { usePannableCanvasContext } from '../Canvas/pannableCanvasContext'
import { FilterChip, Pressable } from '../Common'

const MotionPressable = motion.create(Pressable)
import RadarRoundedIcon from '@mui/icons-material/RadarRounded'
import HistoryRoundedIcon from '@mui/icons-material/HistoryRounded'
import ScheduleRoundedIcon from '@mui/icons-material/ScheduleRounded'
import ReportProblemRoundedIcon from '@mui/icons-material/ReportProblemRounded'
import { useTimeline } from '../../context/useTimeline'
import { useClientPreferences } from '../../context/useClientPreferences'
import type { DashboardViewProps } from '../../types/dashboardView'
import type { TimelineElement } from '../../types/timelineElement'
import type { DashboardChecklistVisualItem } from '../../types/dashboardVisualItem'
import {
  elementEndMs,
  elementStartMs,
  getElementColor,
  getHoverTitle,
  getLayoutTransition,
  splitMacroElements,
} from './viewUtils'

const TAU = Math.PI * 2
const DAY_MS = 86_400_000

const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value))

type RadarMode = 'active' | 'overall'
type RadarQuadrant = 'TODO' | 'IN_PROGRESS' | 'ASSESSING' | 'COMPLETE'
type RadarKind = 'GOAL' | 'TASK' | 'SUBTASK' | 'CHECKLIST'
type OutsideBucket = 'PAST_DUE' | 'UNSCHEDULED'

interface RadarNode {
  id: string
  kind: RadarKind
  title: string
  quadrant: RadarQuadrant | null
  outside: OutsideBucket | null
  startMs: number | null
  endMs: number | null
  priority: number
  progress: number
  color: string
  hoverInfo: string
  open: () => void
}

interface QuadrantMeta {
  id: RadarQuadrant
  label: string
  hue: string
  angleStart: number
}

const QUADRANTS: ReadonlyArray<QuadrantMeta> = [
  { id: 'TODO',        label: 'To Do',       hue: 'var(--info)',    angleStart: -Math.PI / 2 },
  { id: 'IN_PROGRESS', label: 'In Progress', hue: 'var(--warning)', angleStart: 0             },
  { id: 'ASSESSING',   label: 'Assessing',   hue: 'var(--chart-1)', angleStart:  Math.PI / 2  },
  { id: 'COMPLETE',    label: 'Complete',    hue: 'var(--success)', angleStart:  Math.PI      },
]

const QUADRANT_SPAN = TAU / QUADRANTS.length

const quadrantMeta = (id: RadarQuadrant): QuadrantMeta =>
  QUADRANTS.find((quadrant) => quadrant.id === id) ?? QUADRANTS[0]

const midAngle = (id: RadarQuadrant): number => quadrantMeta(id).angleStart + QUADRANT_SPAN / 2

const classifyElement = (
  element: TimelineElement,
  todayMs: number,
): { quadrant: RadarQuadrant | null; outside: OutsideBucket | null } => {
  const start = elementStartMs(element)
  const end = elementEndMs(element)
  const hasSchedule = Number.isFinite(start) && start > 0
  if (element.boardStatus === 'COMPLETE') return { quadrant: 'COMPLETE', outside: null }
  if (!hasSchedule) return { quadrant: null, outside: 'UNSCHEDULED' }
  if (Number.isFinite(end) && end > 0 && end < todayMs) return { quadrant: null, outside: 'PAST_DUE' }
  if (element.boardStatus === 'IN_PROGRESS') return { quadrant: 'IN_PROGRESS', outside: null }
  return { quadrant: 'TODO', outside: null }
}

const classifyChecklist = (
  item: DashboardChecklistVisualItem,
  todayMs: number,
): { quadrant: RadarQuadrant | null; outside: OutsideBucket | null } => {
  const status = item.workflowStatus
  if (status === 'COMPLETED' || status === 'NOT_REQUIRED') return { quadrant: 'COMPLETE', outside: null }
  if (item.isUnscheduled) return { quadrant: null, outside: 'UNSCHEDULED' }
  const endRaw = item.endDate ?? item.startDate
  const endMs = endRaw ? new Date(endRaw).getTime() : null
  switch (status) {
    case 'ASSESSING':
      return { quadrant: 'ASSESSING', outside: null }
    case 'POSTPONED':
      return { quadrant: 'TODO', outside: null }
    case 'ONGOING':
      if (endMs != null && Number.isFinite(endMs) && endMs < todayMs) return { quadrant: null, outside: 'PAST_DUE' }
      return { quadrant: 'IN_PROGRESS', outside: null }
    case 'CREATED':
    case 'ASSIGNED':
    case 'ACCEPTED':
      if (endMs != null && Number.isFinite(endMs) && endMs < todayMs) return { quadrant: null, outside: 'PAST_DUE' }
      return { quadrant: 'TODO', outside: null }
    default:
      return { quadrant: null, outside: null }
  }
}

const hashRatio = (id: string, salt = '') => {
  const seed = `${id}#${salt}`
  let hash = 2166136261
  for (let index = 0; index < seed.length; index++) {
    hash ^= seed.charCodeAt(index)
    hash = Math.imul(hash, 16777619)
  }
  return ((hash >>> 0) % 10000) / 10000
}

/* Sizing hierarchy — hard rule:
   subtask.start < subtask.end < task.start < task.end < goal.start < goal.end.
   Ensures a child's end dot is always smaller than its parent's start dot,
   so the visual weight naturally reflects the hierarchy from any radial
   position. Checklists are single-dot only (per user's spec). */
type DotEnd = 'START' | 'END'
const DOT_SIZE: Record<Exclude<RadarKind, 'CHECKLIST'>, Record<DotEnd, number>> = {
  GOAL:    { START: 32, END: 44 },
  TASK:    { START: 20, END: 28 },
  SUBTASK: { START: 12, END: 16 },
}
const CHECKLIST_SIZE = 14

const kindDotSize = (kind: RadarKind, end: DotEnd, scale: number) => {
  if (kind === 'CHECKLIST') return CHECKLIST_SIZE * scale
  return DOT_SIZE[kind][end] * scale
}

const kindZ = (kind: RadarKind) => {
  switch (kind) {
    case 'GOAL':      return 34
    case 'TASK':      return 32
    case 'SUBTASK':   return 30
    case 'CHECKLIST': return 28
  }
}

const formatDay = (ms: number) =>
  new Intl.DateTimeFormat('en', { month: 'short', day: 'numeric' }).format(new Date(ms))

const formatMonthYear = (ms: number) =>
  new Intl.DateTimeFormat('en', { month: 'short' }).format(new Date(ms)).toUpperCase()

const formatRelativeDays = (days: number) => {
  if (days <= 0) return 'today'
  if (days < 1) return 'later today'
  if (days === 1) return 'tomorrow'
  if (days < 7) return `in ${Math.round(days)} days`
  if (days < 30) return `in ${Math.round(days / 7)} wk`
  return `in ${Math.round(days / 30)} mo`
}

interface RingSpec {
  r: number
  label: string
  ms: number
}

/* Build the ring schedule.
   - Active mode: fixed anchors at Today, +7d, +30d, then a repeating monthly
     cadence marching outward — as many as fit inside the canvas' safe radius.
   - Overall mode: two ring anchors (oldest, latest); intermediate rings at
     an interval that keeps the count sensible (weeks / months / quarters). */
const buildActiveRings = (todayMs: number, horizonMs: number, maxItemRadius: number): RingSpec[] => {
  const rings: RingSpec[] = [
    { r: 60,  label: 'Today',      ms: todayMs },
    { r: 140, label: 'This week',  ms: todayMs + 7  * DAY_MS },
    { r: 240, label: 'This month', ms: todayMs + 30 * DAY_MS },
  ]
  const monthSpacing = 120
  let cursor = 240 + monthSpacing
  let monthOffset = 2 // month after "This month"
  while (cursor <= maxItemRadius) {
    const ms = todayMs + monthOffset * 30 * DAY_MS
    rings.push({ r: cursor, label: formatMonthYear(ms), ms })
    cursor += monthSpacing
    monthOffset += 1
    if (monthOffset > 24) break // stop at 2 years out — safety cap
  }
  // Ensure the horizon is reachable inside the canvas even if it's very far.
  if (horizonMs > rings[rings.length - 1].ms) {
    rings[rings.length - 1] = {
      ...rings[rings.length - 1],
      ms: Math.max(rings[rings.length - 1].ms, horizonMs),
    }
  }
  return rings
}

const buildOverallRings = (oldestMs: number, latestMs: number, maxItemRadius: number): RingSpec[] => {
  const inner = 60
  const outer = maxItemRadius
  const spanMs = Math.max(1, latestMs - oldestMs)
  const rangeDays = spanMs / DAY_MS
  let stepMs: number
  let labelFmt: (ms: number) => string
  if (rangeDays > 365) {
    stepMs = 90 * DAY_MS
    labelFmt = formatMonthYear
  } else if (rangeDays > 90) {
    stepMs = 30 * DAY_MS
    labelFmt = formatMonthYear
  } else if (rangeDays > 30) {
    stepMs = 7 * DAY_MS
    labelFmt = formatDay
  } else {
    stepMs = 3 * DAY_MS
    labelFmt = formatDay
  }
  const rings: RingSpec[] = [{ r: inner, label: formatDay(oldestMs), ms: oldestMs }]
  for (let ms = oldestMs + stepMs; ms < latestMs; ms += stepMs) {
    const t = (ms - oldestMs) / spanMs
    const r = inner + t * (outer - inner)
    rings.push({ r, label: labelFmt(ms), ms })
  }
  rings.push({ r: outer, label: formatDay(latestMs), ms: latestMs })
  return rings
}

const timeToRadius = (rings: RingSpec[], targetMs: number, maxItemRadius: number): number => {
  const first = rings[0]
  const last = rings[rings.length - 1]
  if (targetMs <= first.ms) return first.r
  if (targetMs >= last.ms) {
    // Extrapolate beyond the last defined ring so far-future items push into
    // the canvas corners — clamped to the corner radius.
    if (rings.length < 2) return Math.min(last.r, maxItemRadius)
    const prev = rings[rings.length - 2]
    const dR = last.r - prev.r
    const dMs = Math.max(1, last.ms - prev.ms)
    const extraMs = targetMs - last.ms
    return clamp(last.r + (extraMs / dMs) * dR, first.r, maxItemRadius)
  }
  for (let index = 0; index < rings.length - 1; index++) {
    const a = rings[index]
    const b = rings[index + 1]
    if (targetMs >= a.ms && targetMs <= b.ms) {
      const t = (targetMs - a.ms) / Math.max(1, b.ms - a.ms)
      return a.r + t * (b.r - a.r)
    }
  }
  return first.r
}

export const RadarView = ({
  elements,
  visualItems,
  selectedElementId,
  onElementClick,
  onChecklistClick,
  todayContext,
}: DashboardViewProps) => {
  const { calculateElementProgress } = useTimeline()
  const { preferences } = useClientPreferences()
  const { viewportRect } = usePannableCanvasContext()
  const transition = getLayoutTransition(preferences.animationProfile)
  const reducedMotion = preferences.animationProfile === 'REDUCED_MOTION'

  const [mode, setMode] = useState<RadarMode>('active')
  const [hoveredId, setHoveredId] = useState<string | null>(null)
  const [expandedTray, setExpandedTray] = useState<OutsideBucket | null>(null)
  const stageRef = useRef<HTMLDivElement | null>(null)
  const [selfMeasuredSize, setSelfMeasuredSize] = useState({ width: 1200, height: 560 })
  /* Slot in DashboardContainer's controls bar. When present, the toggle and
     tray render there instead of floating on the stage. */
  const controlsSlotEl = useDashboardViewControlsSlot()

  useLayoutEffect(() => {
    const node = stageRef.current
    if (!node) return
    const rect = node.getBoundingClientRect()
    setSelfMeasuredSize({ width: rect.width || 1200, height: rect.height || 560 })
    const observer = new ResizeObserver((entries) => {
      const box = entries[0]?.contentRect
      if (!box) return
      setSelfMeasuredSize({ width: box.width, height: box.height })
    })
    observer.observe(node)
    return () => observer.disconnect()
  }, [])

  const stageSize = useMemo(() => {
    if (viewportRect && viewportRect.width > 0 && viewportRect.height > 0) {
      return { width: viewportRect.width, height: viewportRect.height }
    }
    return selfMeasuredSize
  }, [selfMeasuredSize, viewportRect])

  /* Geometry.
     - Stage IS the canvas — no fixed inner box, so items can spread all the
       way to the visible corners.
     - `maxItemRadius` is the diagonal reach (corner distance).
     - `maxSafeRadius` is the closer edge — used for placing circular
       guides and their labels so they never clip.
     - Scale gently by short-edge so labels/dots stay legible on any shape. */
  const cx = stageSize.width / 2
  const cy = stageSize.height / 2
  const shortEdge = Math.max(1, Math.min(stageSize.width, stageSize.height))
  const scale = clamp(shortEdge / 700, 0.85, 1.28)
  const maxItemRadius = Math.max(120, Math.hypot(cx, cy) - 40)
  const maxSafeRadius = Math.max(100, Math.min(cx, cy) - 60)

  const todayMs = todayContext.todayMs

  const { goals, tasks, subTasks } = useMemo(() => splitMacroElements(elements), [elements])
  const checklistItems = useMemo(
    () => visualItems.filter((item): item is DashboardChecklistVisualItem => item.kind === 'CHECKLIST'),
    [visualItems],
  )

  const nodes: RadarNode[] = useMemo(() => {
    const collected: RadarNode[] = []
    const pushElement = (element: TimelineElement, kind: RadarKind) => {
      const { quadrant, outside } = classifyElement(element, todayMs)
      if (quadrant === null && outside === null) return
      const startMs = elementStartMs(element)
      const endMs = elementEndMs(element)
      collected.push({
        id: element.id,
        kind,
        title: element.title,
        quadrant,
        outside,
        startMs: Number.isFinite(startMs) && startMs > 0 ? startMs : null,
        endMs: Number.isFinite(endMs) && endMs > 0 ? endMs : null,
        priority: element.priority ?? 3,
        progress: calculateElementProgress(element.id),
        color: getElementColor(element, 0),
        hoverInfo: getHoverTitle(element),
        open: () => onElementClick(element),
      })
    }
    for (const goal of goals)       pushElement(goal, 'GOAL')
    for (const task of tasks)       pushElement(task, 'TASK')
    for (const subTask of subTasks) pushElement(subTask, 'SUBTASK')

    for (const item of checklistItems) {
      const { quadrant, outside } = classifyChecklist(item, todayMs)
      if (quadrant === null && outside === null) continue
      const startMs = item.startDate ? new Date(item.startDate).getTime() : null
      const endMs = item.endDate ? new Date(item.endDate).getTime() : null
      collected.push({
        id: item.id,
        kind: 'CHECKLIST',
        title: item.title,
        quadrant,
        outside,
        startMs: startMs != null && Number.isFinite(startMs) ? startMs : null,
        endMs: endMs != null && Number.isFinite(endMs) ? endMs : null,
        priority: item.risk?.severity ?? 3,
        progress: item.isCompleted ? 100 : 0,
        color: item.color,
        hoverInfo: item.title,
        open: () => onChecklistClick(item.id),
      })
    }
    return collected
  }, [goals, tasks, subTasks, checklistItems, todayMs, calculateElementProgress, onElementClick, onChecklistClick])

  const insideNodes = nodes.filter((node) => node.quadrant !== null)
  const outsideNodes = nodes.filter((node) => node.outside !== null)
  const pastDueNodes = outsideNodes.filter((node) => node.outside === 'PAST_DUE')
  const unscheduledNodes = outsideNodes.filter((node) => node.outside === 'UNSCHEDULED')

  const activeHorizonMs = useMemo(() => {
    let horizon = todayMs + 30 * DAY_MS
    for (const node of insideNodes) {
      const target = node.endMs ?? node.startMs
      if (target != null && target > horizon) horizon = target
    }
    return horizon
  }, [insideNodes, todayMs])

  const overallBounds = useMemo(() => {
    let oldest = Number.POSITIVE_INFINITY
    let latest = Number.NEGATIVE_INFINITY
    for (const node of insideNodes) {
      const start = node.startMs ?? node.endMs
      const end = node.endMs ?? node.startMs
      if (start != null && start < oldest) oldest = start
      if (end != null && end > latest) latest = end
    }
    if (!Number.isFinite(oldest) || !Number.isFinite(latest)) {
      return { oldestMs: todayMs, latestMs: todayMs + DAY_MS }
    }
    return { oldestMs: oldest, latestMs: latest === oldest ? oldest + DAY_MS : latest }
  }, [insideNodes, todayMs])

  /* Ring specs — piecewise time-to-radius mapping. Item radii read directly
     off these; ring labels also derive from these. */
  const rings = useMemo(() => {
    if (mode === 'active') return buildActiveRings(todayMs, activeHorizonMs, maxItemRadius)
    return buildOverallRings(overallBounds.oldestMs, overallBounds.latestMs, maxItemRadius)
  }, [mode, todayMs, activeHorizonMs, overallBounds, maxItemRadius])

  /* Coordinates — full capsule geometry per node.
     - Same angle for both start and end dots (they live on one radial ray).
     - Primary = the "hard" (solid) end for the current mode: END in Active,
       START in Overall. If the primary date is missing, we fall back to
       the other end automatically — a single-date item just becomes a
       lone dot with no capsule.
     - Past-started items in Active mode: startR clamps to 0, start dot is
       suppressed (`startSize = 0`), and the tangents converge into the
       origin — the "sucked into today" visual. */
  const nodeCoordinates = useMemo(() => {
    interface NodeGeom {
      angle: number
      primary: DotEnd
      primaryR: number
      primarySize: number
      primaryX: number
      primaryY: number
      secondaryR: number | null
      secondarySize: number
      secondaryX: number | null
      secondaryY: number | null
      /** Two SVG line segments — [x1a, y1a, x2a, y2a] and [x1b, y1b, x2b, y2b] */
      tangents: [number, number, number, number, number, number, number, number] | null
      suckedIn: boolean
    }
    const coords = new Map<string, NodeGeom>()
    const bandFloor = 46
    const capsuleThresholdPx = 25

    for (const node of insideNodes) {
      if (!node.quadrant) continue
      const meta = quadrantMeta(node.quadrant)
      const inset = QUADRANT_SPAN * 0.07
      const usable = QUADRANT_SPAN - 2 * inset
      const angle = meta.angleStart + inset + hashRatio(node.id, 'ang') * usable
      const cosA = Math.cos(angle)
      const sinA = Math.sin(angle)
      const jitter = (hashRatio(node.id, 'rad') - 0.5) * 24

      // Raw radii from time
      const endR = node.endMs != null ? timeToRadius(rings, node.endMs, maxItemRadius) : null
      let startR = node.startMs != null ? timeToRadius(rings, node.startMs, maxItemRadius) : null

      // Past-started (Active mode only): pull start into the origin.
      const suckedIn = mode === 'active' && node.startMs != null && node.startMs <= todayMs
      if (suckedIn) startR = 0

      // Decide primary. Active → END wins if present; Overall → START wins.
      // If the mode's preferred end is missing, use the other.
      let primary: DotEnd
      if (mode === 'active') primary = endR != null ? 'END' : 'START'
      else                   primary = startR != null ? 'START' : 'END'

      const primaryR_raw = primary === 'END' ? endR : startR
      if (primaryR_raw == null) continue // no dates at all — skip (shouldn't happen for items with a quadrant)
      const primaryR = clamp((primaryR_raw ?? 0) + jitter, bandFloor, maxItemRadius)

      const primarySize = kindDotSize(node.kind, primary, scale)

      // Secondary — the other end, if present and different enough to
      // warrant a capsule. For non-checklist kinds only.
      const secondaryEnd: DotEnd = primary === 'END' ? 'START' : 'END'
      const secondaryR_raw = secondaryEnd === 'END' ? endR : startR
      const capsuleEligible = node.kind !== 'CHECKLIST' && secondaryR_raw != null

      let secondaryR: number | null = null
      let secondarySize = 0
      let secondaryX: number | null = null
      let secondaryY: number | null = null
      let tangents: NodeGeom['tangents'] = null

      if (capsuleEligible) {
        // For sucked-in items the start dot itself is suppressed (size 0)
        // but the tangents still converge to r=0.
        secondaryR = suckedIn && secondaryEnd === 'START' ? 0 : secondaryR_raw!
        secondarySize = suckedIn && secondaryEnd === 'START'
          ? 0
          : kindDotSize(node.kind, secondaryEnd, scale)
        secondaryX = cosA * secondaryR
        secondaryY = sinA * secondaryR

        // Radial capsule tangents. In a local frame with the radial ray as
        // +x-axis, inner circle sits at (d1, 0) with size s1, outer at
        // (d2, 0) with size s2 (d2 > d1). External tangents slope at angle
        // β where sin(β) = (s2 - s1) / (d2 - d1); when s1 = s2 they are
        // parallel offset ±s from the axis. Rotate the four tangent points
        // back to world space via the item's angle.
        const dInner = Math.min(primaryR, secondaryR)
        const dOuter = Math.max(primaryR, secondaryR)
        const sInner = dInner === primaryR ? primarySize / 2 : secondarySize / 2
        const sOuter = dOuter === primaryR ? primarySize / 2 : secondarySize / 2
        const radial = dOuter - dInner
        if (radial >= capsuleThresholdPx) {
          const sinB = radial > 0 ? clamp((sOuter - sInner) / radial, -1, 1) : 0
          const cosB = Math.sqrt(Math.max(0, 1 - sinB * sinB))
          // Local tangent points (inner then outer, for each side)
          const localTop: [number, number, number, number] = [
            dInner - sInner * sinB,  sInner * cosB,
            dOuter - sOuter * sinB,  sOuter * cosB,
          ]
          const localBot: [number, number, number, number] = [
            dInner - sInner * sinB, -sInner * cosB,
            dOuter - sOuter * sinB, -sOuter * cosB,
          ]
          const rotate = (lx: number, ly: number): [number, number] => [
            lx * cosA - ly * sinA,
            lx * sinA + ly * cosA,
          ]
          const [ax, ay] = rotate(localTop[0], localTop[1])
          const [bx, by] = rotate(localTop[2], localTop[3])
          const [cx, cy] = rotate(localBot[0], localBot[1])
          const [dx, dy] = rotate(localBot[2], localBot[3])
          tangents = [ax, ay, bx, by, cx, cy, dx, dy]
        }
      }

      coords.set(node.id, {
        angle,
        primary,
        primaryR,
        primarySize,
        primaryX: cosA * primaryR,
        primaryY: sinA * primaryR,
        secondaryR,
        secondarySize,
        secondaryX,
        secondaryY,
        tangents,
        suckedIn,
      })
    }
    return coords
  }, [insideNodes, mode, rings, maxItemRadius, todayMs, scale])

  const todayRingRadius = useMemo(() => {
    if (mode !== 'overall') return null
    return timeToRadius(rings, todayMs, maxItemRadius)
  }, [mode, rings, todayMs, maxItemRadius])

  const isEmphasized = (id: string) => selectedElementId === id || hoveredId === id
  const emphasize = (id: string | null) => setHoveredId(id)

  if (nodes.length === 0) {
    return (
      <div className="radarEmpty">
        <RadarRoundedIcon fontSize="small" />
        <span>No radar items yet.</span>
      </div>
    )
  }

  const handleNodeKey = (event: KeyboardEvent<HTMLElement>, open: () => void) => {
    if (event.key !== 'Enter' && event.key !== ' ') return
    event.preventDefault()
    open()
  }

  return (
    <section className="radarView" id="radar-view">
      <div
        ref={stageRef}
        className={`radarStage radarStage--${mode}`}
        data-no-dashboard-drag="true"
      >
        <CanvasOverlay anchor="viewport" className="radarScanOverlay">
          {/* Sweep — sized to the diagonal so its glow reaches the corners */}
          {!reducedMotion && (
            <motion.div
              aria-hidden="true"
              className="radarSweep"
              style={{
                width: maxItemRadius * 2,
                height: maxItemRadius * 2,
                marginLeft: -maxItemRadius,
                marginTop: -maxItemRadius,
              }}
              animate={{ rotate: 360 }}
              transition={{ repeat: Infinity, ease: 'linear', duration: 32 }}
            />
          )}

          <svg
            className="radarSvg"
            width={stageSize.width}
            height={stageSize.height}
            viewBox={`0 0 ${Math.max(1, stageSize.width)} ${Math.max(1, stageSize.height)}`}
            aria-hidden="true"
          >
            <defs>
              {QUADRANTS.map((quadrant) => (
                <radialGradient
                  key={`grad-${quadrant.id}`}
                  id={`radarQuadGrad-${quadrant.id}`}
                  cx={cx}
                  cy={cy}
                  r={maxItemRadius}
                  gradientUnits="userSpaceOnUse"
                >
                  <stop offset="0%"   style={{ stopColor: quadrant.hue }} stopOpacity="0.08" />
                  <stop offset="70%"  style={{ stopColor: quadrant.hue }} stopOpacity="0.03" />
                  <stop offset="100%" style={{ stopColor: quadrant.hue }} stopOpacity="0" />
                </radialGradient>
              ))}
              {/* Comet-trail gradient for past-started items — bright at the
                  origin ("black hole"), fading outward to the primary dot.
                  Defined once, reused per sucked-in path. */}
              <radialGradient
                id="radarSuckFade"
                cx={cx}
                cy={cy}
                r={maxItemRadius}
                gradientUnits="userSpaceOnUse"
              >
                <stop offset="0%"   style={{ stopColor: 'var(--warning)' }} stopOpacity="0.55" />
                <stop offset="40%"  style={{ stopColor: 'var(--warning)' }} stopOpacity="0.20" />
                <stop offset="100%" style={{ stopColor: 'var(--warning)' }} stopOpacity="0.02" />
              </radialGradient>
            </defs>
            {/* Quadrant tints — rectangular fills clipped to each screen quadrant.
                Cleaner than pie-wedges when items live outside the outer ring. */}
            <g>
              <rect x={cx} y={0}  width={stageSize.width - cx} height={cy} fill={`url(#radarQuadGrad-TODO)`} />
              <rect x={cx} y={cy} width={stageSize.width - cx} height={stageSize.height - cy} fill={`url(#radarQuadGrad-IN_PROGRESS)`} />
              <rect x={0}  y={cy} width={cx} height={stageSize.height - cy} fill={`url(#radarQuadGrad-ASSESSING)`} />
              <rect x={0}  y={0}  width={cx} height={cy} fill={`url(#radarQuadGrad-COMPLETE)`} />
            </g>
            <g transform={`translate(${cx} ${cy})`}>
            {/* Concentric guide rings. Drawn only if they fit inside the
                short-edge safe radius so they never clip the frame. Items
                are NOT constrained to these — they can live in the corners
                beyond the outermost ring. */}
            {rings.map((ring, index) => {
              if (ring.r > maxSafeRadius) return null
              const isFirst = index === 0
              const isLast = index === rings.length - 1
              return (
                <circle
                  key={`ring-${index}`}
                  r={ring.r}
                  fill="none"
                  style={{ stroke: `color-mix(in srgb, var(--info) ${isFirst || isLast ? '16' : '14'}%, transparent)` }}
                  strokeWidth={isFirst || isLast ? 1 : 0.75}
                  strokeDasharray={isFirst || isLast ? undefined : '3 6'}
                />
              )
            })}

            {/* Quadrant divider lines — full-length crosshair through the
                entire stage, not just to the outer ring. */}
            {QUADRANTS.map((quadrant) => {
              const endLen = maxItemRadius + 200
              return (
                <line
                  key={`div-${quadrant.id}`}
                  x1={0}
                  y1={0}
                  x2={Math.cos(quadrant.angleStart) * endLen}
                  y2={Math.sin(quadrant.angleStart) * endLen}
                  style={{ stroke: 'color-mix(in srgb, var(--info) 14%, transparent)' }}
                  strokeWidth={0.9}
                />
              )
            })}

            {/* Origin ring — small halo behind whatever the center marker is */}
            <circle r={24} style={{ fill: 'color-mix(in srgb, var(--info) 5%, transparent)', stroke: 'color-mix(in srgb, var(--info) 24%, transparent)' }} strokeWidth={0.75} />
            {mode === 'active' && (
              <>
                <circle r={11} style={{ fill: 'color-mix(in srgb, var(--warning) 22%, transparent)' }} />
                <circle r={5} style={{ fill: 'var(--warning)' }} />
              </>
            )}
            {mode === 'overall' && (
              <circle r={3.5} style={{ fill: 'color-mix(in srgb, var(--info) 85%, transparent)' }} />
            )}

            {/* Overall mode: today ring */}
            {mode === 'overall' && todayRingRadius != null && todayRingRadius <= maxSafeRadius && (
              <>
                <motion.circle
                  r={todayRingRadius}
                  fill="none"
                  style={{ stroke: 'var(--warning)' }}
                  strokeOpacity={0.95}
                  strokeWidth={2}
                  strokeDasharray="5 7"
                  animate={reducedMotion ? undefined : { strokeDashoffset: [0, -24] }}
                  transition={reducedMotion ? undefined : { repeat: Infinity, duration: 2.6, ease: 'linear' }}
                />
                <circle
                  r={todayRingRadius}
                  fill="none"
                  style={{ stroke: 'color-mix(in srgb, var(--warning) 16%, transparent)' }}
                  strokeWidth={7}
                />
              </>
            )}

            {/* Capsule chrome — two distinct visuals depending on state.
                (a) Sucked-in (Active + past-started): a filled comet-trail
                    triangle from the primary dot's tangent edges converging
                    to a single point at the origin. The radialGradient fill
                    fades from bright at origin to faint at the primary edge,
                    so the item reads as being pulled into the "black hole"
                    of today. No animation — the shape alone tells the story.
                (b) Normal capsule: dashed twin circle at the secondary
                    position + two dashed tangent lines — the "connected
                    circles" look from your sketch.
                Drawn under the HTML primary dots (SVG z-index 10, HTML nodes
                z-index 28-44). Emphasize class brightens both. */}
            {insideNodes.map((node) => {
              if (node.kind === 'CHECKLIST') return null
              const coord = nodeCoordinates.get(node.id)
              if (!coord) return null
              if (coord.secondaryR == null) return null
              if (!coord.tangents) return null
              const emphasized = isEmphasized(node.id)
              const meta = quadrantMeta(node.quadrant!)
              const t = coord.tangents
              if (coord.suckedIn) {
                // Triangle from origin to the primary circle's two tangent
                // points. Note: tangents array is [innerTopX, innerTopY,
                // outerTopX, outerTopY, innerBotX, innerBotY, outerBotX,
                // outerBotY]. Inner points are the "point" side; outer are
                // the primary-circle edges. For sucked-in, both inner
                // points collapse to (0, 0), so we draw a triangle:
                // origin → outerTop → outerBot → origin.
                const path = `M 0,0 L ${t[2].toFixed(2)},${t[3].toFixed(2)} L ${t[6].toFixed(2)},${t[7].toFixed(2)} Z`
                return (
                  <g
                    key={`capsule-${node.id}`}
                    className={`radarCapsule isSuckedIn${emphasized ? ' isEmphasized' : ''}`}
                  >
                    <path
                      className="radarCapsuleTrail"
                      d={path}
                      fill="url(#radarSuckFade)"
                    />
                    {/* Tiny void anchor — a bright dot at origin so the
                        convergence point reads unmistakably. */}
                    <circle
                      className="radarCapsuleVoidAnchor"
                      cx={0}
                      cy={0}
                      r={2.5}
                    />
                  </g>
                )
              }
              return (
                <g
                  key={`capsule-${node.id}`}
                  className={`radarCapsule${emphasized ? ' isEmphasized' : ''}`}
                  style={{ stroke: meta.hue }}
                >
                  <line
                    className="radarCapsuleTangent"
                    x1={t[0]} y1={t[1]}
                    x2={t[2]} y2={t[3]}
                  />
                  <line
                    className="radarCapsuleTangent"
                    x1={t[4]} y1={t[5]}
                    x2={t[6]} y2={t[7]}
                  />
                  {coord.secondarySize > 0 && coord.secondaryX != null && coord.secondaryY != null && (
                    <circle
                      className="radarCapsuleTwin"
                      cx={coord.secondaryX}
                      cy={coord.secondaryY}
                      r={coord.secondarySize / 2}
                    />
                  )}
                </g>
              )
            })}
            </g>
          </svg>
        </CanvasOverlay>

        <CanvasOverlay anchor="viewport">
          {/* Center marker — DOT sits at true viewport center (0,0), label
              floats just below it as an independent absolute element so it
              never pushes the dot off center. */}
          <div className="radarOriginDot" style={{ left: cx, top: cy }} aria-hidden="true" />
          {mode === 'active' ? (
            <div className="radarOriginLabel radarOriginLabel--today" style={{ left: cx, top: cy + 26 }}>
              <em>Today</em>
            </div>
          ) : (
            <div className="radarOriginLabel radarOriginLabel--oldest" style={{ left: cx, top: cy + 26 }}>
              <span>Oldest</span>
              <em>{formatDay(overallBounds.oldestMs)}</em>
            </div>
          )}

          {/* Today chip (Overall) — pinned to the top of the today ring */}
          {mode === 'overall' && todayRingRadius != null && todayRingRadius <= maxSafeRadius && (
            <div
              className="radarTodayChip"
              style={{ left: cx, top: cy - todayRingRadius }}
            >
              <span aria-hidden="true" />
              Today · {formatDay(todayMs)}
            </div>
          )}

          {/* Ring labels — only for rings that fit */}
          {rings.map((ring, index) => {
            if (ring.r > maxSafeRadius) return null
            return (
              <span
                key={`ring-label-${index}`}
                className="radarRingLabel"
                style={{ left: cx, top: cy - ring.r }}
              >
                {ring.label}
              </span>
            )
          })}

          {/* Quadrant labels — corner-anchored via the sign of the mid-angle's
              direction vector. In SVG/screen coords `sin(angle) > 0` means the
              wedge points DOWN (bottom half) and `cos(angle) > 0` means it
              points RIGHT. Testing sign correctly places COMPLETE
              (mid-angle 5π/4 → cos<0, sin<0) in the top-left; the previous
              `angle > 0` check mislabelled it to bottom-left. */}
          {QUADRANTS.map((quadrant) => {
            const angle = midAngle(quadrant.id)
            const dx = Math.cos(angle)
            const dy = Math.sin(angle)
            const inset = 20
            const isRight = dx > 0
            const isBottom = dy > 0
            return (
              <span
                key={`quad-label-${quadrant.id}`}
                className="radarQuadrantLabel"
                style={{
                  left: isRight ? stageSize.width - inset : inset,
                  top: isBottom ? stageSize.height - inset : inset,
                  transform: `translate(${isRight ? '-100%' : '0'}, ${isBottom ? '-100%' : '0'})`,
                  color: `color-mix(in srgb, ${quadrant.hue} 60%, var(--text-strong))`,
                  borderColor: `color-mix(in srgb, ${quadrant.hue} 42%, transparent)`,
                  background: `color-mix(in srgb, ${quadrant.hue} 14%, transparent)`,
                }}
              >
                {quadrant.label}
              </span>
            )
          })}
        </CanvasOverlay>

        {/* Nodes */}
        <AnimatePresence>
          {insideNodes.map((node) => {
            const coord = nodeCoordinates.get(node.id)
            if (!coord) return null
            const emphasized = isEmphasized(node.id)
            const size = coord.primarySize
            const z = kindZ(node.kind) + (emphasized ? 10 : 0)
            const meta = quadrantMeta(node.quadrant!)
            const secondary = mode === 'active'
              ? (node.endMs != null ? formatRelativeDays((node.endMs - todayMs) / DAY_MS) : 'no end')
              : (node.startMs != null ? formatDay(node.startMs) : '')
            return (
              <MotionPressable
                key={node.id}
                layoutId={`radar-${node.id}`}
                layout
                transition={transition}
                className={`radarNode radarNode--${node.kind.toLowerCase()}${emphasized ? ' isEmphasized' : ''}`}
                aria-label={`${node.kind.toLowerCase()} ${node.title}`}
                style={{
                  left: cx + coord.primaryX,
                  top: cy + coord.primaryY,
                  width: size,
                  height: size,
                  zIndex: z,
                  backgroundColor: node.color,
                  boxShadow: emphasized
                    ? `0 0 0 3px color-mix(in srgb, ${meta.hue} 60%, transparent), var(--shadow-card)`
                    : `0 0 0 2px color-mix(in srgb, ${meta.hue} 34%, transparent), var(--shadow-card)`,
                }}
                onClick={(event) => { event.stopPropagation(); node.open() }}
                onKeyDown={(event) => handleNodeKey(event, node.open)}
                onPointerEnter={() => emphasize(node.id)}
                onPointerLeave={() => emphasize(null)}
                onFocus={() => emphasize(node.id)}
                onBlur={() => emphasize(null)}
                data-hover-info={node.hoverInfo}
              >
                {node.kind === 'GOAL' && (
                  <span className="radarNodeProgress">{node.progress}%</span>
                )}
                {emphasized && (
                  <motion.span
                    layout
                    className="radarNodeTooltip"
                    style={{ borderColor: `color-mix(in srgb, ${meta.hue} 55%, transparent)` }}
                  >
                    <strong>{node.title}</strong>
                    <em>{meta.label} · {secondary}</em>
                  </motion.span>
                )}
              </MotionPressable>
            )
          })}
        </AnimatePresence>

      </div>

      {/* Portal Radar-specific controls (mode toggle + tray) into the
          DashboardContainer's controls slot when available. Fallback: if
          the slot is missing (e.g. mobile shell with a different bar),
          render inline just above the stage so behavior degrades cleanly. */}
      {controlsSlotEl
        ? createPortal(
            <RadarSlotControls
              mode={mode}
              onModeChange={setMode}
              reducedMotion={reducedMotion}
              pastDueNodes={pastDueNodes}
              unscheduledNodes={unscheduledNodes}
              expandedTray={expandedTray}
              onExpandedTrayChange={setExpandedTray}
            />,
            controlsSlotEl,
          )
        : (
          <RadarSlotControls
            mode={mode}
            onModeChange={setMode}
            reducedMotion={reducedMotion}
            pastDueNodes={pastDueNodes}
            unscheduledNodes={unscheduledNodes}
            expandedTray={expandedTray}
            onExpandedTrayChange={setExpandedTray}
            inlineFallback
          />
        )}
    </section>
  )
}

const RadarSlotControls = ({
  mode,
  onModeChange,
  reducedMotion,
  pastDueNodes,
  unscheduledNodes,
  expandedTray,
  onExpandedTrayChange,
  inlineFallback = false,
}: {
  mode: RadarMode
  onModeChange: (mode: RadarMode) => void
  reducedMotion: boolean
  pastDueNodes: RadarNode[]
  unscheduledNodes: RadarNode[]
  expandedTray: OutsideBucket | null
  onExpandedTrayChange: (bucket: OutsideBucket | null) => void
}
  & { inlineFallback?: boolean }
) => {
  const hasTrayItems = pastDueNodes.length > 0 || unscheduledNodes.length > 0
  return (
    <div className={`radarSlotControls${inlineFallback ? ' radarSlotControls--inline' : ''}`}>
      <div className="radarModeToggle" role="tablist" aria-label="Radar mode">
        {(['active', 'overall'] as const).map((option) => {
          const active = mode === option
          return (
            <Pressable
              key={option}
              role="tab"
              aria-selected={active}
              className={`radarModeButton${active ? ' isActive' : ''}`}
              onClick={() => onModeChange(option)}
            >
              {active && (
                <motion.span
                  layoutId="radarModeIndicator"
                  className="radarModeIndicator"
                  transition={reducedMotion ? { duration: 0 } : { type: 'spring', stiffness: 320, damping: 32 }}
                />
              )}
              <span className="radarModeButtonLabel">
                {option === 'active' ? <RadarRoundedIcon fontSize="inherit" /> : <HistoryRoundedIcon fontSize="inherit" />}
                {option === 'active' ? 'Active' : 'Overall'}
              </span>
            </Pressable>
          )
        })}
      </div>
      {hasTrayItems && (
        <div className="radarSlotTray">
          {pastDueNodes.length > 0 && (
            <RadarTrayGroup
              label="Past-due"
              count={pastDueNodes.length}
              tone="warn"
              icon={<ReportProblemRoundedIcon fontSize="inherit" />}
              nodes={pastDueNodes}
              expanded={expandedTray === 'PAST_DUE'}
              onToggle={() => onExpandedTrayChange(expandedTray === 'PAST_DUE' ? null : 'PAST_DUE')}
            />
          )}
          {unscheduledNodes.length > 0 && (
            <RadarTrayGroup
              label="Unscheduled"
              count={unscheduledNodes.length}
              tone="neutral"
              icon={<ScheduleRoundedIcon fontSize="inherit" />}
              nodes={unscheduledNodes}
              expanded={expandedTray === 'UNSCHEDULED'}
              onToggle={() => onExpandedTrayChange(expandedTray === 'UNSCHEDULED' ? null : 'UNSCHEDULED')}
            />
          )}
        </div>
      )}
    </div>
  )
}

export const RadarTrayGroup = ({
  label,
  count,
  tone,
  icon,
  nodes,
  expanded,
  onToggle,
}: {
  label: string
  count: number
  tone: 'warn' | 'neutral'
  icon: React.ReactNode
  nodes: RadarNode[]
  expanded: boolean
  onToggle: () => void
}) => (
  <div className={`radarTrayGroup radarTrayGroup--${tone}${expanded ? ' isExpanded' : ''}`}>
    <FilterChip
      selected={expanded}
      count={count}
      onClick={onToggle}
      aria-expanded={expanded}
      aria-label={`${label}: ${count} item${count === 1 ? '' : 's'}`}
    >
      {icon}
      <span>{label}</span>
    </FilterChip>
    <AnimatePresence initial={false}>
      {expanded && (
        <motion.div
          className="radarTrayList"
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: 'auto', opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={{ duration: 0.22, ease: 'easeOut' }}
        >
          {nodes.map((node) => (
            <Pressable
              key={node.id}
              className="radarTrayItem"
              onClick={node.open}
              title={node.title}
            >
              <span className="radarTrayItemDot" style={{ backgroundColor: node.color }} aria-hidden="true" />
              <span className="radarTrayItemTitle">{node.title}</span>
            </Pressable>
          ))}
        </motion.div>
      )}
    </AnimatePresence>
  </div>
)
